import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, Shield } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeSwitcher } from "@/components/ThemeSwitcher";
import { ModeToggle } from "@/components/ModeToggle";
import { useAuth } from "@/context/AuthContext";

const NAV_LINKS = [
  { to: "/justicebot", label: "JusticeVerse AI" },
  { to: "/counsel", label: "Counsel AI" },
  { to: "/casebrief", label: "CaseBrief AI" },
  { to: "/verse-translation", label: "Verse Translation" },
  { to: "/prepmate", label: "PrepMate AI" },
  { to: "/knowledge-hub", label: "Articles & Daily Digest" },
  { to: "/careers", label: "Careers" },
  { to: "/pricing", label: "Subscription" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header
      className="fixed top-0 inset-x-0 z-50 border-b border-hairline bg-canvas/80 backdrop-blur-xl"
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-20 flex items-center justify-between">
        <Link to="/" data-testid="nav-home-link">
          <Logo size="sm" />
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.to.replace("/", "")}`}
              className={`font-mono text-[11px] uppercase tracking-[0.14em] whitespace-nowrap transition-colors ${
                location.pathname === l.to ? "text-gold" : "text-inkmuted hover:text-ink"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden xl:flex items-center gap-4 shrink-0 ml-8">
          <ModeToggle />
          <ThemeSwitcher />
          {user ? (
            <>
              {user.role === "admin" && (
                <Link
                  to="/admin"
                  data-testid="nav-admin-link"
                  className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-gold hover:opacity-80"
                >
                  <Shield className="w-4 h-4" /> Owner
                </Link>
              )}
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-link"
                className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.15em] text-inkmuted hover:text-ink"
              >
                <LayoutDashboard className="w-4 h-4" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="rounded-full bg-surface border border-hairline text-ink hover:bg-surface2 px-5 py-2 text-xs font-semibold transition-all"
              >
                <LogOut className="w-3.5 h-3.5 inline mr-1" /> Sign Out
              </button>
            </>
          ) : (
            <>
              <Link
                to="/login"
                data-testid="nav-login-link"
                className="font-mono text-[11px] uppercase tracking-[0.18em] text-inkmuted hover:text-ink"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-register-link"
                className="rounded-full bg-btn text-btnink hover:bg-btnhover px-6 py-2.5 text-xs font-semibold uppercase tracking-wider transition-all"
              >
                Get Started
              </Link>
            </>
          )}
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <ModeToggle />
          <ThemeSwitcher />
          {!user && (
            <>
              <Link
                to="/login"
                data-testid="nav-mobile-login-link"
                className="font-mono text-[11px] uppercase tracking-[0.14em] text-inkmuted hover:text-ink px-2"
              >
                Sign In
              </Link>
              <Link
                to="/register"
                data-testid="nav-mobile-register-link"
                className="rounded-full bg-btn text-btnink hover:bg-btnhover px-4 py-2 text-[11px] font-semibold uppercase tracking-wider transition-all"
              >
                Sign Up
              </Link>
            </>
          )}
          <button
            className="text-ink"
            onClick={() => setOpen(!open)}
            data-testid="nav-mobile-toggle"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="xl:hidden border-t border-hairline bg-canvas px-5 py-6 space-y-5" data-testid="mobile-menu">
          {NAV_LINKS.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`mobile-link-${l.to.replace("/", "")}`}
              className="block font-mono text-sm uppercase tracking-[0.18em] text-inkmuted"
            >
              {l.label}
            </Link>
          ))}
          <div className="pt-4 border-t border-hairline space-y-3">
            {user ? (
              <>
                {user.role === "admin" && (
                  <Link to="/admin" onClick={() => setOpen(false)} className="block font-mono text-sm uppercase tracking-[0.15em] text-gold">
                    Owner Dashboard
                  </Link>
                )}
                <Link to="/dashboard" onClick={() => setOpen(false)} className="block font-mono text-sm uppercase tracking-[0.15em] text-inkmuted">
                  My Workspace
                </Link>
                <button onClick={handleLogout} className="font-mono text-sm uppercase tracking-[0.15em] text-inkmuted">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="block font-mono text-sm uppercase tracking-[0.18em] text-inkmuted">
                  Sign In
                </Link>
                <Link
                  to="/register"
                  onClick={() => setOpen(false)}
                  className="inline-block rounded-full bg-btn text-btnink px-6 py-2.5 text-xs font-semibold uppercase tracking-wider"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
